import { createContext } from 'react';

import { FullPageContent } from '@/components/layout/full-page-content';
import { useCurrentAsset } from '@/hooks/use-current-asset';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useMe } from '@/hooks/use-me';
import { IWarrantyRepository } from '@/warranty/domain/repositories/warranty.repository';

import { ConditionViolationSection } from '../components/condition-violations/condition-violation-timeseries-chart';
import { FilterBar } from '../components/filters/filter-bar';
import { GuaranteeGaugeSection } from '../components/guarantees/guarantee-state-section';
import { OverviewSection } from '../components/overview-section/overview-section';
// import { PenaltyDetails } from '../components/penalty-details/penalty-details';
import { PenaltyOverview } from '../components/penalty-overview/penalty-overview';

function WarrantyNotConfiguredMessage({ message }: { message: string }) {
  return (
    <div
      className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-6 text-center text-gray-600"
      data-testid="warranty-not-configured"
    >
      {message}
    </div>
  );
}

export const WarrantyDetailsContext = createContext<
  { fullFrontendId: string; id: string } | undefined
>(undefined);

export function WarrantyDetailsPage() {
  const [filters, setFilters] =
    IWarrantyRepository.useGetFiltersFromQueryParams();

  const { asset } = useCurrentAsset();
  const me = useMe();
  const featureFlag = useFeatureFlag();

  const assetId = asset?.id ?? '';
  const penaltiesNotConfigured =
    me.config.warrantyAssetIdsWithoutPenalties?.includes(assetId) ?? false;
  const conditionsNotConfigured =
    me.config.warrantyAssetIdsWithoutConditions?.includes(assetId) ?? false;

  if (!asset && featureFlag('newNavigation')) {
    return null;
  }

  return (
    <WarrantyDetailsContext.Provider
      value={{
        fullFrontendId: asset?.fullFrontendId ?? filters.siteId ?? '',
        id: asset?.id ?? '',
      }}
    >
      <FullPageContent
        testId="warranty-page"
        className="min-h-[calc(100vh-64px)] w-full overflow-auto p-8"
        scrollable
      >
        <div className="container mx-auto flex flex-col space-y-8">
          {/* Section: Filter Bar */}
          <section aria-label="Filter Bar" className="flex space-y-4">
            <FilterBar
              filters={filters}
              onChange={(filters) => setFilters.push(filters)}
            />
          </section>
          {/* Section: Guarantee Gauges */}
          <section
            id="guarantee-gauges"
            aria-label="Guarantee Gauges"
            className="overflow-x-auto"
          >
            <GuaranteeGaugeSection />
          </section>
          {/* Section: OverviewTable */}
          <section id="overview-table" aria-label="Overview Table">
            <OverviewSection filters={filters} />
          </section>
          {/* Section: Warranty Penalties */}
          <section
            id="penalty-chart"
            aria-label="Warranty Penalties"
            className="overflow-x-auto"
          >
            {penaltiesNotConfigured ? (
              <WarrantyNotConfiguredMessage message="Penalties are not defined for this site's warranty." />
            ) : (
              <PenaltyOverview filters={filters} />
            )}
          </section>
          {/* Section: Penalty Details */}
          {/* <section
              id="penalty-details"
              aria-label="Penalty Details"
              className="w-full overflow-x-auto"
            >
              <PenaltyDetails filters={filters} />
            </section> */}
          {/* Section: Condition Violations */}
          <section
            id="condition-violations"
            aria-label="Condition Violations"
            className="overflow-x-auto"
          >
            {conditionsNotConfigured ? (
              <WarrantyNotConfiguredMessage message="Conditions are not defined for this site's warranty." />
            ) : (
              <ConditionViolationSection filters={filters} />
            )}
          </section>
        </div>
      </FullPageContent>
    </WarrantyDetailsContext.Provider>
  );
}
WarrantyDetailsPage.displayName = 'WarrantyDetailsPage';
